import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuBriefcaseBusiness, LuCalendarDays, LuChevronRight, LuEye, LuEyeOff, LuFolderKanban, LuTriangleAlert } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import { useTranslation } from '../../../i18n/LanguageContext';
import { EmptyState, HelpNote, SectionHeader } from '../components/ProfileUi';
import ProjectVisitsModal from '../ProjectVisitsModal';

/**
 * A project counts as passive in two cases: the user's role in it is passive
 * (status 'passive' on the assignment) or the project itself is passive.
 */
export const isPassiveProject = (project) => project?.status === 'passive' || project?.projectStatus === 'Pasif';

/**
 * "Projelerim": the projects the user works on, with their type (primary /
 * backup) and blinding; a project opens its visits.
 */
const ProjectsSection = ({ projects, employeeId }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.projects.${key}`, vars);
  const [showPassiveProjects, setShowPassiveProjects] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showVisitsModal, setShowVisitsModal] = useState(false);

  const passiveCount = projects.filter(isPassiveProject).length;
  const shown = projects.filter((project) => showPassiveProjects || !isPassiveProject(project));

  return (
    <div className="tw-space-y-4">
      <SectionHeader
        icon={LuBriefcaseBusiness}
        gradient="tw-from-indigo-400 tw-to-blue-600 tw-shadow-indigo-500/30"
        title={label('title')}
        description={label('description')}
      >
        {passiveCount > 0 && (
          <Button size="sm" onClick={() => setShowPassiveProjects(!showPassiveProjects)} aria-pressed={!showPassiveProjects}>
            {showPassiveProjects ? <LuEyeOff aria-hidden="true" className="tw-h-4 tw-w-4" /> : <LuEye aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {showPassiveProjects ? label('hidePassive', { count: passiveCount }) : label('showPassive', { count: passiveCount })}
          </Button>
        )}
      </SectionHeader>

      {projects.some((project) => !project.workerType || !project.blindStatus) && <HelpNote tone="warning">{label('missingInfoHint')}</HelpNote>}

      {shown.length === 0 ? (
        <EmptyState icon={LuFolderKanban} title={projects.length === 0 ? label('empty') : label('allPassiveHidden')} text={projects.length === 0 ? label('emptyHint') : undefined} />
      ) : (
        <ul className="tw-grid tw-gap-3 sm:tw-grid-cols-2 xl:tw-grid-cols-3">
          {shown.map((project, index) => {
            const passive = isPassiveProject(project);
            return (
              // Aynı projede birden çok merkez/rol satırı olabilir; sıra anahtara katılır
              // eslint-disable-next-line react/no-array-index-key
              <li key={`${project.id || 'project'}-${index}`} className="tw-block">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedProject(project);
                    setShowVisitsModal(true);
                  }}
                  className={`tw-group tw-flex tw-h-full tw-w-full tw-flex-col tw-gap-3 tw-rounded-2xl tw-border tw-p-4 tw-text-left tw-transition-[background-color,border-color,box-shadow] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                    passive
                      ? 'tw-border-dashed tw-border-slate-300 tw-bg-slate-50/60 hover:tw-bg-slate-50 dark:tw-border-white/15 dark:tw-bg-white/[0.02]'
                      : 'tw-border-slate-200 tw-bg-white/75 hover:tw-border-teal-400/40 hover:tw-bg-white hover:tw-shadow-[0_12px_30px_-20px_rgba(13,148,136,0.6)] dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:hover:tw-bg-white/[0.07]'
                  }`}
                >
                  <span className="tw-flex tw-items-start tw-gap-3">
                    <span className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-xl ${passive ? 'tw-bg-slate-500/10 tw-text-slate-400' : 'tw-bg-indigo-500/10 tw-text-indigo-600 dark:tw-text-indigo-300'}`}>
                      <LuFolderKanban aria-hidden="true" className="tw-h-5 tw-w-5" />
                    </span>
                    <span className="tw-min-w-0 tw-flex-1">
                      <span className={`tw-block tw-break-words tw-text-sm tw-font-semibold ${passive ? 'tw-text-slate-500 dark:tw-text-slate-400' : 'tw-text-slate-900 dark:tw-text-white'}`}>{project.titleShort}</span>
                      {project.projectDate && (
                        <span className="tw-mt-0.5 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                          <LuCalendarDays aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                          {project.projectDate}
                        </span>
                      )}
                      {project.centerName && <span className="tw-mt-0.5 tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{project.centerName}</span>}
                    </span>
                    <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-300 tw-transition-transform group-hover:tw-translate-x-0.5 group-hover:tw-text-teal-500 dark:tw-text-slate-600" />
                  </span>
                  <span className="tw-flex tw-flex-wrap tw-gap-1.5">
                    {project.workerType && <Badge tone={project.workerType === 'primer' ? 'info' : 'neutral'}>{project.workerType === 'primer' ? label('primer') : label('backup')}</Badge>}
                    {project.blindStatus && <Badge tone={project.blindStatus === 'blinded' ? 'accent' : 'warning'}>{project.blindStatus === 'blinded' ? label('blinded') : label('unblinded')}</Badge>}
                    {(!project.workerType || !project.blindStatus) && (
                      <Badge tone="danger"><LuTriangleAlert aria-hidden="true" className="tw-h-3 tw-w-3" />{label('missingInfo')}</Badge>
                    )}
                    {passive && (
                      <Badge tone="neutral">
                        <span className="tw-inline" title={project.status === 'passive' ? label('passiveRole') : label('passiveProject')}>{label('passive')}</span>
                      </Badge>
                    )}
                  </span>
                  {passive && <span className="tw-text-[11px] tw-text-slate-400 dark:tw-text-slate-500">{project.status === 'passive' ? label('passiveRole') : label('passiveProject')}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {selectedProject && (
        <ProjectVisitsModal
          show={showVisitsModal}
          onHide={() => setShowVisitsModal(false)}
          project={selectedProject}
          employeeId={employeeId}
        />
      )}
    </div>
  );
};

ProjectsSection.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object).isRequired,
  employeeId: PropTypes.string,
};

export default ProjectsSection;
